import { useCallback, useState } from "react";
import Dropzone from "react-dropzone";
import { Button } from "../../components/ui/button";
import { CircleCheckBig, FileText, Upload, X } from "lucide-react";
import { fetchFastApi } from "../../api/fastapi";
import { toast } from "sonner";
import { useTemplatesV2 } from "../../hooks/useTemplatesV2.ts";
import { useNavigate } from "react-router-dom";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
  CommandGroup,
} from "../../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import { cn } from "../../lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import { useAuth } from "../../contexts/authContext.tsx";
import { validateFileSizesWithToast } from "../../lib/fileUploadValidation";

export const FileParserUploadV2 = () => {
  const [sourceFiles, setSourceFiles] = useState<File[] | null>(null);
  const [schema, setSchema] = useState<string | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [schemaPopoverOpen, setSchemaPopoverOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const { schemas } = useTemplatesV2({ fetchOnLoad: true });

  const onSourceFileDrop = (acceptedFiles: File[]) => {
    const validFiles = validateFileSizesWithToast(acceptedFiles);

    if (validFiles.length === 0) return;

    if (sourceFiles) {
      setSourceFiles([...sourceFiles, ...validFiles]);
    } else {
      setSourceFiles(validFiles);
    }
  };

  const extractAsync = useCallback(async () => {
    setLoading(true);

    try {
      const formData = new FormData();

      sourceFiles?.forEach((file) => {
        formData.append("files", file);
      });

      formData.append("schema_id", schema || "");

      await fetchFastApi({
        method: "POST",
        path: "/extract/async",
        body: formData,
      });

      navigate("/dashboard/extraction/extractions");

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error uploading files:", error);
      toast.error("Error extracting files");
    }
    setLoading(false);
  }, [sourceFiles, schema, navigate]);

  const onSubmit = useCallback(async () => {
    const currentSchema = schemas?.find((s) => s.id === schema);

    if (!currentSchema) {
      toast.error("Please select a target template");
      return;
    }

    await extractAsync();
  }, [schema, schemas, extractAsync]);

  const deleteSourceFile = (index: number) => {
    const newFiles = sourceFiles?.filter((_, i) => i !== index);
    if (newFiles) {
      setSourceFiles(newFiles);
    } else {
      setSourceFiles(null);
    }
  };

  if (isSubmitted) {
    return (
      <div className="h-screen flex flex-col items-center justify-center w-full py-12 px-20 bg-[url('/gradient_bg.png')] bg-no-repeat bg-top bg-cover">
        <div className="max-w-2xl w-full">
          <div className="flex flex-col items-center">
            <CircleCheckBig size={32} />
            <div className="text-xl">Files submitted successfully</div>
            <div className="text-lg">
              You will receive an email with the extracted data shortly
            </div>
            <Button
              className="mt-6"
              onClick={() => {
                setIsSubmitted(false);
                setSourceFiles(null);
                setSchema(undefined);
              }}
            >
              Submit another
            </Button>
          </div>
        </div>
      </div>
    );
  }

  enum FileType {
    BMP = "bmp",
    CSV = "csv",
    DOCX = "docx",
    JPEG = "jpeg",
    JPG = "jpg",
    PDF = "pdf",
    PNG = "png",
    TXT = "txt",
    XLSM = "xlsm",
    XLSX = "xlsx",
    XLS = "xls",
  }

  const acceptedFileTypes = {
    "application/pdf": [".pdf"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
    "image/bmp": [".bmp"],
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
      ".xlsx",
    ],
    "application/vnd.ms-excel.sheet.macroEnabled.12": [".xlsm"],
    "application/vnd.ms-excel": [".xls"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
    "text/csv": [".csv"],
  };

  const acceptedFileExtensions = Object.values(FileType)
    .map((type) => `.${type}`)
    .join(",");

  return (
    <div className="h-screen flex flex-col items-center justify-center w-full bg-[url('/gradient_bg.png')] bg-no-repeat bg-top bg-cover">
      <div className="max-w-2xl w-full py-12 px-20 ">
        <div className="flex flex-col">
          <div className="text-blue-700">Midship</div>
          <div className="text-3xl mt-2">Extract data</div>

          <div className="flex gap-4 mt-6">
            <div className="flex flex-col w-1/2 gap-4 p-4 rounded-lg bg-stone-100">
              <div className="text-lg">Source Files</div>
              {sourceFiles &&
                sourceFiles.map((file, index) => (
                  <UploadedFile
                    key={index}
                    file={file}
                    deleteFile={() => deleteSourceFile(index)}
                  />
                ))}

              <Dropzone onDrop={onSourceFileDrop} accept={acceptedFileTypes}>
                {({ getRootProps, getInputProps }) => (
                  <section>
                    <div
                      {...getRootProps()}
                      className="flex rounded-md gap-2 items-center p-2 bg-white border border-stone-200 cursor-pointer"
                      role="button"
                      tabIndex={0}
                    >
                      <input
                        {...getInputProps()}
                        accept={acceptedFileExtensions}
                      />
                      <Upload size={20} />
                      <p className="text-sm">Upload</p>
                    </div>
                  </section>
                )}
              </Dropzone>
            </div>
            <div className="flex flex-col w-1/2 gap-4 p-4 rounded-lg bg-stone-100">
              <div className="text-lg">Target Template</div>
              <Popover
                open={schemaPopoverOpen}
                onOpenChange={setSchemaPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={schemaPopoverOpen}
                    className="w-full justify-between hover:bg-white"
                  >
                    <span className="truncate font-normal">
                      {schema
                        ? schemas?.find((s) => s.id === schema)?.name
                        : "Select template..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command
                    className="max-h-[300px] w-[250px] overflow-y-auto"
                    filter={(id, query) => {
                      if (
                        schemas
                          ?.find((s) => s.id === id)
                          ?.name.toLowerCase()
                          .includes(query.toLowerCase())
                      ) {
                        return 1;
                      }
                      return 0;
                    }}
                  >
                    <CommandInput placeholder="Search template..." />
                    <CommandEmpty>No template found.</CommandEmpty>
                    <CommandList>
                      {(schemas || []).some((s) => s.userId === user?.id) && (
                        <CommandGroup heading="My Templates">
                          {(schemas || [])
                            .filter((s) => s.userId === user?.id)
                            .map((schemaItem) => (
                              <CommandItem
                                key={schemaItem.id}
                                value={schemaItem.id}
                                onSelect={() => {
                                  setSchema(schemaItem.id);
                                  setSchemaPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    schemaItem.id === schema
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <span>{schemaItem.name}</span>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      )}
                      {schemas?.some((s) => s.userId !== user?.id) && (
                        <CommandGroup heading="Workspace Templates">
                          {(schemas || [])
                            .filter((s) => s.userId !== user?.id)
                            .map((schemaItem) => (
                              <CommandItem
                                key={schemaItem.id}
                                value={schemaItem.id}
                                onSelect={() => {
                                  setSchema(schemaItem.id);
                                  setSchemaPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    schemaItem.id === schema
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <span>{schemaItem.name}</span>
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex">
            <Button className="mt-6 w-40" onClick={onSubmit} loading={loading}>
              Extract
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UploadedFile = ({
  file,
  deleteFile,
}: {
  file: File;
  deleteFile: () => void;
}) => (
  <div className="flex items-center justify-between gap-2 p-2 bg-white rounded-md group border border-stone-200">
    <div className="flex items-center gap-2 min-w-0 flex-1">
      <FileText size={20} className="shrink-0" />
      <div className="truncate">{file.name}</div>
    </div>

    <X
      size={20}
      className="shrink-0 cursor-pointer invisible group-hover:visible"
      onClick={deleteFile}
    />
  </div>
);
